import type { CustodyCase, NoteSheet } from '../types/case';
import type { AppConfig } from '../types/config';
import { categoryLabel } from './caseStatus';
import { bn, fmtNumeric, taka } from './bn';

export function noteFileNo(pcr: string): string {
  return `৪৪.০৭.২৬৯০.০১৫.২৬.${bn(pcr.slice(-3))}`;
}

export function combinedFileNo(seq: number): string {
  return `৪৪.০৭.২৬৯০.০১৫.২৬.${bn(String(seq).padStart(3, '0'))}`;
}

export function fileDivision(cases: CustodyCase[]): string {
  const set = Array.from(new Set(cases.map((c) => c.division)));
  return set.length === 1 ? set[0] : `একাধিক বিভাগ (${bn(set.length)}টি)`;
}

/** Auto-generates the formal numbered note body for one or more PCR cases. */
export function buildNoteBody(cases: CustodyCase[], config: AppConfig): string {
  const total = cases.reduce((s, c) => s + c.fine + c.extraCharge, 0);
  const online = cases.filter((c) => c.payment.source === 'online').length;
  const office = cases.length - online;
  if (cases.length === 1) {
    const c = cases[0];
    return [
      `<p>উপর্যুক্ত বিষয়ে জানানো যাচ্ছে যে, PCR নং ${c.pcr} অনুযায়ী ${c.regNo} নম্বরের একটি ${categoryLabel(c.category, config)} গত ${fmtNumeric(c.createdAt, '/')} তারিখে “${c.reason}” জনিত কারণে হেফাজতে গ্রহণ করা হয়।</p>`,
      `<p><b>২।</b> সংশ্লিষ্ট যানবাহনের মালিক কর্তৃক প্রয়োজনীয় কাগজপত্র দাখিল করা হয়েছে এবং প্রযোজ্য জরিমানার অর্থ পরিশোধের বিষয়টি সিস্টেমে নিশ্চিত হয়েছে।</p>`,
      `<p><b>৩।</b> দাখিলকৃত কাগজপত্র যাচাই ও প্রয়োজনীয় কার্যক্রম সম্পন্ন হওয়ায়, প্রযোজ্য বিধি ও নির্দেশনা অনুযায়ী যানবাহনটি অবমুক্তির অনুমোদনের জন্য নথি উপস্থাপন করা হলো।</p>`,
    ].join('');
  }
  return [
    `<p>উপর্যুক্ত বিষয়ে জানানো যাচ্ছে যে, ডিএমপি ট্রাফিক বিভাগ কর্তৃক বিভিন্ন কারণে হেফাজতে গৃহীত ${bn(cases.length)}টি যানবাহনের (বিস্তারিত তফসিল-“ক”তে বর্ণিত) মালিকগণ অবমুক্তির জন্য প্রয়োজনীয় কাগজপত্র দাখিল করেছেন।</p>`,
    `<p><b>২।</b> তফসিলভুক্ত প্রতিটি যানবাহনের প্রযোজ্য জরিমানা বাবদ সর্বমোট ${taka(total)} পরিশোধ ও সমন্বয়ের বিষয়টি সিস্টেমে নিশ্চিত হয়েছে (অনলাইনে ${bn(online)}টি, অফিসে সরকারি চালানের মাধ্যমে ${bn(office)}টি)।</p>`,
    `<p><b>৩।</b> যাচাইকারী কর্মকর্তা কর্তৃক দাখিলকৃত কাগজপত্র মূল কপির সাথে মিলিয়ে যাচাই সম্পন্ন হয়েছে এবং কোনো কেসে অনিষ্পন্ন আপত্তি নেই।</p>`,
    `<p><b>৪।</b> এমতাবস্থায়, প্রযোজ্য বিধি ও নির্দেশনা অনুযায়ী তফসিলভুক্ত যানবাহনসমূহ একত্রে অবমুক্তির অনুমোদনের জন্য নথি উপস্থাপন করা হলো।</p>`,
  ].join('');
}

/** Auto-generates a formal Bangla note sheet from PCR data. */
export function generateNote(c: CustodyCase, config: AppConfig): NoteSheet {
  const docs = c.documents.filter((d) => d.status === 'verified').map((d) => d.name).join(', ');
  const pay = c.payment.source === 'office'
    ? `অফিসে সরকারি চালান নং ${c.payment.challan ?? '—'} এর মাধ্যমে`
    : `অনলাইনে লেনদেন নং ${c.payment.txnId ?? '—'} এর মাধ্যমে`;
  return {
    fileNo: c.noteSheet?.fileNo ?? noteFileNo(c.pcr),
    subject: 'আটককৃত যানবাহন অবমুক্তির অনুমোদন প্রসঙ্গে।',
    paragraphs: [
      `উপর্যুক্ত বিষয়ে জানানো যাচ্ছে যে, PCR নং ${c.pcr} অনুযায়ী ${c.regNo} নম্বরের একটি ${categoryLabel(c.category, config)} গত ${fmtNumeric(c.createdAt, '/')} তারিখে “${c.reason}” জনিত কারণে ${c.road} এলাকা হতে হেফাজতে গ্রহণ করা হয় এবং বর্তমানে ${c.station?.station ?? c.assignedStation}-এ সংরক্ষিত রয়েছে।`,
      `সংশ্লিষ্ট যানবাহনের মালিক ${c.ownerName} কর্তৃক প্রয়োজনীয় কাগজপত্র (${docs || 'তালিকা সংযুক্ত'}) দাখিল করা হয়েছে এবং প্রযোজ্য জরিমানার অর্থ ${pay} পরিশোধের বিষয়টি সিস্টেমে নিশ্চিত হয়েছে।`,
      'দাখিলকৃত কাগজপত্র যাচাই ও প্রয়োজনীয় কার্যক্রম সম্পন্ন হওয়ায়, প্রযোজ্য বিধি ও নির্দেশনা অনুযায়ী যানবাহনটি অবমুক্তির অনুমোদনের জন্য নথি উপস্থাপন করা হলো।',
    ],
    observation: c.verification?.remarks ?? 'দাখিলকৃত সকল কাগজপত্র মূল কপির সাথে মিলিয়ে দেখা হয়েছে এবং সঠিক পাওয়া গেছে।',
    recommendation: 'সদয় অনুমোদনের জন্য পেশ করা হলো।',
    forwards: c.noteSheet?.forwards ?? [],
  };
}
